import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { BellRing, Send, Settings2, Save } from 'lucide-react';


export const ReminderCard = ({ onAuthError }) => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cfg, setCfg] = useState({ hour: 8, minute: 0, stale_days: 2, enabled: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/reminders/status`);
      setStatus(data);
      setCfg(data.config);
    } catch (err) { onAuthError(err); }
  }, [onAuthError]);

  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post(`/reminders/send-now`, {});
      toast({ title: data.sent ? 'Recordatorio enviado' : 'Sin pendientes', description: data.message });
      load();
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo enviar el recordatorio.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.put(`/reminders/config`, cfg);
      toast({ title: 'Configuración guardada', description: cfg.enabled ? `Recordatorio a las ${String(cfg.hour).padStart(2, '0')}:${String(cfg.minute).padStart(2, '0')} con ${cfg.stale_days} día(s) de espera.` : 'Recordatorio desactivado.' });
      setEditing(false);
      load();
    } catch (err) {
      if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo guardar la configuración.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const lastSent = status?.last_sent?.sent_at ? new Date(status.last_sent.sent_at + 'Z').toLocaleString('es-VE') : 'Nunca';
  const timeLabel = status?.config ? `${String(status.config.hour).padStart(2, '0')}:${String(status.config.minute).padStart(2, '0')}` : '08:00';

  return (
    <div className="bg-gradient-to-br from-amber-500/15 to-orange-500/5 border border-amber-500/20 rounded-3xl p-6" data-testid="admin-reminder-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="h-12 w-12 shrink-0 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-400 flex items-center justify-center shadow-lg">
          <BellRing className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold">Recordatorio diario de seguimiento</h2>
          <p className="text-slate-400 text-sm" data-testid="reminder-schedule-text">
            {status?.config?.enabled === false
              ? <span className="text-red-300">Desactivado.</span>
              : <>Cada día a las <span className="text-white">{timeLabel}</span> (Venezuela) recibes un email con los leads en etapa "Nuevo" que llevan más de <span className="text-white">{status?.stale_days ?? 2}</span> día(s) sin contactar.</>}
          </p>
          <p className="text-sm mt-2">
            <span data-testid="reminder-pending-count" className="font-bold text-amber-300">{status ? status.pending_count : '—'}</span>
            <span className="text-slate-400"> pendientes ahora · Último envío: </span>
            <span data-testid="reminder-last-sent" className="text-slate-200">{lastSent}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="reminder-config-btn" variant="outline" onClick={() => setEditing((v) => !v)} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
            <Settings2 className="h-4 w-4 mr-2" /> Configurar
          </Button>
          <Button data-testid="reminder-send-now-btn" onClick={sendNow} disabled={sending} className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
            <Send className="h-4 w-4 mr-2" /> {sending ? 'Enviando...' : 'Enviar ahora'}
          </Button>
        </div>
      </div>

      {editing && (
        <form onSubmit={saveConfig} className="mt-6 pt-6 border-t border-white/10 grid sm:grid-cols-5 gap-4 items-end" data-testid="reminder-config-form">
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Hora (Venezuela)</label>
            <Input data-testid="reminder-hour-input" type="time" value={`${String(cfg.hour).padStart(2, '0')}:${String(cfg.minute).padStart(2, '0')}`}
              onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setCfg((c) => ({ ...c, hour: h || 0, minute: m || 0 })); }}
              className="h-11 bg-slate-900/70 border-white/10 text-white rounded-xl" required />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">Días de espera sin contacto</label>
            <Input data-testid="reminder-days-input" type="number" min="1" max="30" value={cfg.stale_days} onChange={(e) => setCfg((c) => ({ ...c, stale_days: Math.min(30, Math.max(1, Number(e.target.value) || 1)) }))} className="h-11 bg-slate-900/70 border-white/10 text-white rounded-xl" required />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 h-11">
            <input type="checkbox" data-testid="reminder-enabled-checkbox" checked={cfg.enabled} onChange={(e) => setCfg((c) => ({ ...c, enabled: e.target.checked }))} className="h-4 w-4 rounded" />
            Recordatorio activo
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300 h-11">
            <input type="checkbox" data-testid="reminder-weekly-checkbox" checked={cfg.weekly_enabled !== false} onChange={(e) => setCfg((c) => ({ ...c, weekly_enabled: e.target.checked }))} className="h-4 w-4 rounded" />
            Resumen semanal (lunes)
          </label>
          <Button type="submit" data-testid="reminder-config-save" disabled={saving} className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500">
            <Save className="h-4 w-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </form>
      )}
    </div>
  );
};

export default ReminderCard;
